import { themePalette } from "../../theme";

export const DISABLED_OPACITY = 0.5;

export const NESTED_OPTION_INDENT = "36px";

/** Offset MUI checkbox ripple so options flush to the section edge. */
const CHECKBOX_RIPPLE_PADDING = "-9px";

export const unselectedCheckboxSx = {
  color: themePalette.greyLight,
} as const;

export const flushCheckboxSx = {
  ...unselectedCheckboxSx,
  marginTop: CHECKBOX_RIPPLE_PADDING,
  marginBottom: CHECKBOX_RIPPLE_PADDING,
  marginLeft: CHECKBOX_RIPPLE_PADDING,
  marginRight: "3px",
} as const;

/** Use the design's 24px slot; the adjacent label is also a click target. */
export const testPanelRadioSx = {
  ...unselectedCheckboxSx,
  p: 0,
  mr: "12px",
  "&.Mui-checked": {
    color: themePalette.redMedium,
  },
} as const;

export const flushFormControlLabelSx = {
  "& .MuiFormControlLabel-root": {
    alignItems: "flex-start",
    marginLeft: CHECKBOX_RIPPLE_PADDING,
  },
} as const;

/** Show only the column for the current device breakpoint. */
export const getResponsiveColumnDisplaySx = (deviceType: string) => ({
  display: {
    xs: deviceType !== "mobile" ? "none" : "flex",
    sm: deviceType === "mobile" ? "none" : "flex",
  },
  flexDirection: "column" as const,
});

export const columnStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
} as const;

export const columnFlexSx = {
  display: "flex",
  flexDirection: "column",
} as const;

export const fullWidthSx = {
  width: "100%",
} as const;

export const flushTopSx = {
  "&&": { pt: 0 },
} as const;

export const carrierSectionTopGapSx = {
  "&&": { pt: 2 },
  "@media (min-width: 769px)": {
    "&&": { pt: "40px" },
  },
} as const;

export const gridBottomPaddingSx = {
  pb: 3,
} as const;

export const aquaDividerBaseSx = {
  borderColor: themePalette.darkAqua20,
  width: "100%",
} as const;

export const aquaDividerSpacedSx = {
  my: 4,
  ...aquaDividerBaseSx,
} as const;

export const aquaDividerTightSx = {
  my: 1,
  ...aquaDividerBaseSx,
} as const;

export const aneuploidyVariantDividerSx = {
  my: 2,
  ...aquaDividerBaseSx,
  color: themePalette.greyBase,
  fontFamily: "bold",
  fontSize: "14px",
  "&::before, &::after": {
    borderColor: themePalette.darkAqua20,
  },
} as const;

export const checkboxLabelRowMarginSx = {
  mb: { xs: 3, sm: 1 },
} as const;

export const cfDnaOptOutRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  flexWrap: "nowrap" as const,
  minWidth: 0,
  "@media (min-width: 769px)": {
    justifyContent: "flex-end",
  },
};

export const cfDnaInfoIconSx = {
  color: themePalette.greyMedium,
  width: 16,
  height: 16,
  ml: "4px",
  display: "block",
  cursor: "pointer",
} as const;

export const cfDnaCheckboxSx = {
  ...unselectedCheckboxSx,
  p: "9px",
  my: "-9px",
  mr: "-9px",
  ml: "4px",
  "&.Mui-checked": {
    color: themePalette.redMedium,
  },
} as const;

export const cfDnaTooltipPopperSx = {
  "& .MuiTooltip-tooltip": {
    backgroundColor: "white",
    boxShadow: "0px 10px 20px rgb(0 0 0 / 10%)",
    zIndex: 100000,
  },
} as const;

export const carrierPanelRowPySx = {
  py: "4px",
} as const;

export const carrierPanelRowPtSx = {
  pt: "4px",
} as const;

export const lightFormLabelSx = {
  fontFamily: "light",
} as const;

export const clickableClinicalLabelSx = {
  fontFamily: "light",
  color: themePalette.blueDark,
  cursor: "pointer",
} as const;

export const phlebotomyBoxSx = {
  marginTop: "24px",
  paddingBottom: "10px",
} as const;

export const phlebotomyLanguageSelectSx = {
  marginTop: "4px",
  minWidth: 200,
} as const;

export const otherLanguageFieldSx = {
  flexGrow: 1,
} as const;

export const getDisabledOpacitySx = (disabled: boolean) => ({
  opacity: disabled ? DISABLED_OPACITY : 1,
});

export const getCarrierPanelLabelSx = (disabled: boolean) =>
  ({
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? DISABLED_OPACITY : 1,
    m: 0,
    p: 0,
    lineHeight: "24px",
    alignSelf: "flex-start",
    flex: 1,
    minWidth: 0,
  }) as const;

export const getUnityCompleteFormControlSx = (disabled: boolean) => ({
  maxWidth: 320,
  opacity: disabled ? DISABLED_OPACITY : 1,
});

export const unityCompleteMenuItemSx = {
  whiteSpace: "normal",
  "@media (min-width: 769px)": {
    whiteSpace: "nowrap",
  },
} as const;

export const unityCompleteMenuRadioSx = {
  p: 0,
  mr: "10px",
  pointerEvents: "none",
} as const;

export const getNestedAddOnColumnSx = (
  deviceType: string,
  disabled: boolean,
) => ({
  ...getResponsiveColumnDisplaySx(deviceType),
  ...flushFormControlLabelSx,
  pl: NESTED_OPTION_INDENT,
  gap: 1,
  opacity: disabled ? DISABLED_OPACITY : 1,
});

export const getCfDnaOptOutWrapperSx = (
  deviceType: string,
  indent: boolean,
) => ({
  ...getResponsiveColumnDisplaySx(deviceType),
  ...(indent ? { pl: NESTED_OPTION_INDENT } : null),
});

export const getAneuploidyChoiceColumnSx = (
  deviceType: string,
  disabled: boolean,
) => ({
  ...getResponsiveColumnDisplaySx(deviceType),
  gap: 1,
  ...flushFormControlLabelSx,
  pl: NESTED_OPTION_INDENT,
  opacity: disabled ? DISABLED_OPACITY : 1,
});

export const getAneuploidyLabelColumnSx = (disabled: boolean) => ({
  display: "flex",
  flexDirection: "column" as const,
  flex: 1,
  minWidth: 0,
  opacity: disabled ? DISABLED_OPACITY : 1,
});

export const getAneuploidyLabelCursorSx = (disabled: boolean) => ({
  cursor: disabled ? "default" : "pointer",
});

export const getPhlebotomyLanguageRowSx = (
  isOtherLanguageSelected: boolean,
) => ({
  paddingRight: "10px",
  paddingLeft: "24px",
  display: "flex",
  alignItems: "baseline",
  gap: 2,
  justifyContent: isOtherLanguageSelected ? "flex-start" : "flex-end",
});

export const getRhdLabelPointerSx = (blocked: boolean) => ({
  ...clickableClinicalLabelSx,
  pointerEvents: blocked ? ("none" as const) : ("auto" as const),
});

export const getPatientInfoGridSx = (py: number) => ({
  py,
});

export const patientCheckboxAlignmentSx = {
  "& .MuiFormControlLabel-root": {
    marginLeft: 0,
  },
};

export const patientGestationColumnSx = {
  ...patientCheckboxAlignmentSx,
  display: "flex",
  flexDirection: "column" as const,
  rowGap: { xs: "0.5rem", sm: "0rem" },
};

export const patientGestationFormGroupSx = {
  ...patientCheckboxAlignmentSx,
  rowGap: { xs: "0.5rem", sm: "0rem" },
};

export const patientSectionDividerSx = {
  my: 2,
  borderStyle: "dashed",
  color: themePalette.greyLight,
} as const;
